import { useQuery } from '@tanstack/react-query';
import { markerService, GetMarkersParams } from '../services/markerService';
import { Marker } from '@citiguide/shared';
import { useEffect, useRef } from 'react';

export const useMarkers = (params?: GetMarkersParams, options?: { refetchInterval?: number }) => {
  // Keep a ref to the last successful data to use as fallback
  const lastDataRef = useRef<Marker[] | undefined>(undefined);
  
  const query = useQuery({
    queryKey: ['markers', params],
    queryFn: () => {
      return markerService.getMarkers(params);
    },
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: options?.refetchInterval ? true : false,
    retry: (failureCount, error: unknown) => {
      // Don't retry on 429 errors - wait for rate limit to reset
      const err = error as { response?: { status?: number } };
      if (err?.response?.status === 429) {
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff, but don't retry 429 errors
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    placeholderData: (previousData) => {
      // Use previous data if available, otherwise use last successful data
      const dataToUse = previousData || lastDataRef.current;
      if (dataToUse) {
        lastDataRef.current = dataToUse;
      }
      return dataToUse;
    },
  });
  
  // Update ref when we get successful data, or preserve data on 429 errors
  useEffect(() => {
    if (query.data && !query.isError) {
      lastDataRef.current = query.data;
    }
  }, [query.data, query.isError, query.error]);
  
  return query;
};

export const useMarker = (markerId: string | null) => {
  return useQuery({
    queryKey: ['marker', markerId],
    queryFn: () => markerService.getMarker(markerId!),
    enabled: !!markerId,
  });
};
